import React, { useState, useMemo } from 'react';
import { ReactFlow, Background, Controls, NodeTypes, useNodesState, useEdgesState } from '@xyflow/react';
import { SpinningLoader, Text, Button, Chip } from '@frontend/design-system';
import '@xyflow/react/dist/style.css';
import { useGetSandboxQuery, useListRegistrationsQuery } from '../queries/sales-demo.queries';
import { buildDagNodes } from '../utils/dag-builder';
import { buildFlowGraph, applyDagreLayout } from '../utils/dag-layout';
import { DagNodeComponent } from './dag/dag-node';

const nodeTypes: NodeTypes = { dagNode: DagNodeComponent as any };

interface SandboxDetailProps {
  sandboxId: string;
  onBack: () => void;
}

export const SandboxDetail: React.FC<SandboxDetailProps> = ({ sandboxId, onBack }) => {
  const [expandedDatasetIndex, setExpandedDatasetIndex] = useState<number | null>(null);

  const { data: sandbox, isLoading: sandboxLoading } = useGetSandboxQuery(sandboxId);
  const { data: registrationsData, isLoading: registrationsLoading } = useListRegistrationsQuery({
    pageSize: 1000,
    pageToken: '',
    filter: '',
  });

  const isLoading = sandboxLoading || registrationsLoading;

  const ephemeralId =
    (sandbox as any)?.ephemeralId ??
    (sandbox as any)?.ephemeral_id ??
    (sandbox as any)?.config?.ephemeralId ??
    (sandbox as any)?.config?.ephemeral_id;
  const ownerUserId =
    (sandbox as any)?.ownerUserId ??
    (sandbox as any)?.owner_user_id ??
    (sandbox as any)?.config?.ownerUserId ??
    (sandbox as any)?.config?.owner_user_id;
  const cfg = (sandbox as any)?.config ?? {};
  // display_name is optional and often unset; fall back to integration/vertical.
  const displayName =
    cfg.displayName ?? cfg.display_name ?? cfg.integrationName ?? cfg.integration_name ?? cfg.vertical ?? '';
  const datasets: any[] = (sandbox as any)?.datasets ?? [];

  const executionTraceData = useMemo(() => {
    if (datasets.length === 0 || !registrationsData?.registrations) return { nodes: [], edges: [] };

    const allDagNodes = buildDagNodes(registrationsData.registrations);
    const sandboxTypeUrls = new Set(datasets.map((d) => d.typeUrl ?? d.type_url ?? '').filter(Boolean));
    const relevantTypeUrls = new Set(sandboxTypeUrls);
    for (const node of allDagNodes) {
      if (sandboxTypeUrls.has(node.typeUrl)) {
        for (const dep of node.dependencies) relevantTypeUrls.add(dep);
      }
    }
    const filtered = allDagNodes.filter((n) => relevantTypeUrls.has(n.typeUrl));
    if (filtered.length === 0) return { nodes: [], edges: [] };

    const { nodes, edges } = buildFlowGraph(filtered);
    return { nodes: applyDagreLayout(nodes, edges), edges };
  }, [datasets, registrationsData?.registrations]);

  const [traceNodes] = useNodesState(executionTraceData.nodes);
  const [traceEdges] = useEdgesState(executionTraceData.edges);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <SpinningLoader />
      </div>
    );
  }

  if (!sandbox) {
    return (
      <div style={{ padding: '16px' }}>
        <Text>Sandbox not found</Text>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0' }}>
        <Button variant='secondary' size='small' iconName='arrow-left' onClick={onBack}>
          Back
        </Button>
        <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />
        {displayName && <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{displayName}</span>}
        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: '#5F6771' }}>
          {sandbox.id}
        </span>
        <Chip variant={ephemeralId ? 'primary' : 'eggplant'} size='tiny'>
          {ephemeralId ? 'Ephemeral' : 'Persistent'}
        </Chip>
        <div style={{ height: '20px', width: '1px', backgroundColor: '#A1A8B0' }} />
        <span style={{ fontSize: '12px', color: '#6F7780' }}>
          {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Info cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
        {ownerUserId && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid #A1A8B0`,
              backgroundColor: 'white',
            }}
          >
            <div style={{ fontSize: '11px', color: '#6F7780', marginBottom: '4px' }}>Owner</div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#333' }}>{ownerUserId}</div>
          </div>
        )}
        {ephemeralId && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid #A1A8B0`,
              backgroundColor: 'white',
            }}
          >
            <div style={{ fontSize: '11px', color: '#6F7780', marginBottom: '4px' }}>Ephemeral ID</div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#333' }}>{ephemeralId}</div>
          </div>
        )}
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '8px',
            border: `1px solid #A1A8B0`,
            backgroundColor: 'white',
          }}
        >
          <div style={{ fontSize: '11px', color: '#6F7780', marginBottom: '4px' }}>Datasets</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' as const }}>
            {datasets.map((d, i) => {
              const url = d.typeUrl ?? d.type_url ?? '';
              return (
                <Chip key={i} variant='neutral' size='tiny'>
                  {url.split('/').pop() || 'unknown'}
                </Chip>
              );
            })}
            {datasets.length === 0 && <span style={{ fontSize: '12px', color: '#8E959E' }}>None</span>}
          </div>
        </div>
      </div>

      {/* Execution Trace DAG */}
      <div
        style={{
          marginBottom: '16px',
          border: `1px solid #A1A8B0`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: `1px solid #CED3DA` }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>Execution Trace</div>
          <div style={{ fontSize: '12px', color: '#6F7780' }}>Dataset dependency flow for this sandbox</div>
        </div>
        <div style={{ height: '350px', position: 'relative' }}>
          {traceNodes.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: '#8E959E',
              }}
            >
              No dataset execution trace available
            </div>
          ) : (
            <ReactFlow
              nodes={traceNodes}
              edges={traceEdges}
              nodeTypes={nodeTypes}
              fitView
              proOptions={{ hideAttribution: true }}
            >
              <Background color='#f9f9f9' />
              <Controls />
            </ReactFlow>
          )}
        </div>
      </div>

      {/* Dataset Details */}
      <div
        style={{
          border: `1px solid #A1A8B0`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: `1px solid #CED3DA` }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>Dataset Details</div>
          <div style={{ fontSize: '12px', color: '#6F7780' }}>
            {datasets.length} dataset{datasets.length !== 1 ? 's' : ''} in this sandbox
          </div>
        </div>
        {datasets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#8E959E' }}>
            No datasets found in this sandbox
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {datasets.map((dataset, index) => {
              const typeUrl = dataset.typeUrl ?? dataset.type_url ?? '';
              const typeName = typeUrl.split('/').pop() || 'unknown';
              const isExpanded = expandedDatasetIndex === index;
              const isLast = index === datasets.length - 1;
              const data = dataset.data || {};
              const hasData = Object.keys(data).length > 0;

              return (
                <div key={index} style={{ borderBottom: isLast ? 'none' : `1px solid #CED3DA` }}>
                  <div
                    style={{
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: hasData ? 'pointer' : 'default',
                    }}
                    onClick={() => hasData && setExpandedDatasetIndex(isExpanded ? null : index)}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>
                      {typeName}
                    </span>
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#6F7780',
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {typeUrl}
                    </span>
                    {hasData ? (
                      <span style={{ flex: '0 0 16px', fontSize: '11px', color: '#8E959E' }}>▾</span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#8E959E' }}>No data</span>
                    )}
                  </div>
                  {isExpanded && hasData && (
                    <div style={{ padding: '0 16px 12px 16px' }} onClick={(e) => e.stopPropagation()}>
                      <pre
                        style={{
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          backgroundColor: '#f9fafb',
                          padding: '12px',
                          borderRadius: '6px',
                          border: `1px solid #CED3DA`,
                          overflow: 'auto',
                          maxHeight: '300px',
                          margin: 0,
                        }}
                      >
                        {JSON.stringify(data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
